import {CommandBase} from "./commandBase";
import {RemoteConstantsModel, Metadata} from "../models/remoteConstants.model";
import {MetadataMigration} from "../utils/migrateRecord";
import {SectionKey} from "../utils/branchDiff";

// Inserts a Metadata record migrated from Other into Current. Bundles the
// record itself plus its satellite data (localization rows, image pack,
// new image rows) so one undo reverts the whole arrival.
//
// All writes are additive — entries that already exist on Current are left
// alone, on the assumption that Current's edits are intentional. The
// classifier upstream has already verified the record's uid isn't in use
// on Current; satellites just check for presence before writing.
export class MigrateMetadataRecordCommand extends CommandBase {
    migration: MetadataMigration;

    constructor(migration: MetadataMigration) {
        super();
        this.migration = migration;
    }

    execute(data: RemoteConstantsModel): boolean {
        const collection = getMetaCollection(data, this.migration.sectionKey);
        if (!collection) return false;
        if (collection.some(m => m.uid === this.migration.record.uid)) {
            return false; // uid already in use (race with classifier — refuse)
        }

        // Deep-clone the record so subsequent edits in Current don't accidentally
        // mutate Other's in-memory state.
        const cloned: Metadata = JSON.parse(JSON.stringify(this.migration.record));
        collection.push(cloned);

        // Localization: add entries that don't already exist on Current.
        if (!data.localization) data.localization = {};
        for (const row of this.migration.locRows) {
            if (!data.localization[row.lang]) data.localization[row.lang] = {};
            if (data.localization[row.lang][row.key] === undefined) {
                data.localization[row.lang][row.key] = row.value;
            }
        }

        // Image pack at the record's uid. Always safe to write because the
        // uid is brand new to Current (verified by the classifier).
        if (this.migration.packRow) {
            if (!data.imagePacks) data.imagePacks = {};
            data.imagePacks[this.migration.record.uid] = JSON.parse(JSON.stringify(this.migration.packRow));
        }

        // Image metadata rows: only those Current didn't already have.
        if (!data.images) data.images = {};
        for (const row of this.migration.imageRows) {
            if (data.images[row.key] === undefined) {
                data.images[row.key] = JSON.parse(JSON.stringify(row.value));
            }
        }

        return true;
    }
}

function getMetaCollection(data: RemoteConstantsModel, sectionKey: SectionKey): Metadata[] | undefined {
    switch (sectionKey) {
        case 'unitAttributes':
            if (!data.unitAttributes) data.unitAttributes = [];
            return data.unitAttributes;
        case 'buildingAttributes':
            if (!data.buildingAttributes) data.buildingAttributes = [];
            return data.buildingAttributes;
        case 'techActionAttributes':
            if (!data.techActionAttributes) data.techActionAttributes = [];
            return data.techActionAttributes;
        case 'techPassiveAttributes':
            if (!data.techPassiveAttributes) data.techPassiveAttributes = [];
            return data.techPassiveAttributes;
        case 'rewards':
            if (!data.rewards) data.rewards = [];
            return data.rewards;
        default:
            return undefined;
    }
}
